import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, Zap, Phone, ChevronRight, Building2, CheckCircle2, Briefcase, Home as HomeIcon, ArrowRight, Loader2, MessageCircle } from "lucide-react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import RevealText from "../components/RevealText";
import BillingTransparency from "../components/BillingTransparency";
import { CITIES } from "../data/cities";
import { whatsappLink } from "../lib/whatsapp";
import { trackCityLandingView, trackCityLandingCtaClick, trackAddressSearch } from "../lib/ga";
import { api } from "../lib/api";
import { enrichWithRentCast } from "../lib/rentcastEnrich";

// ──────────────────────────────────────────────────────────────────────
// Heuristic: residential addresses with #/Apt/Suite/Unit/Ste tokens are
// apartments. Non-residential premise types are commercial. Mirrors the
// homepage `AddressHero` so visitors see the SAME service-type detection
// regardless of where they entered the funnel.
// ──────────────────────────────────────────────────────────────────────
function detectServiceType(addressText, premiseType) {
  const txt = (addressText || "").toLowerCase();
  const hasUnit = /#\s*\w|\bapt\.?\s*\w|\bsuite\s*\w|\bunit\s*\w|\bste\.?\s*\w/i.test(txt);
  const premise = (premiseType || "").toLowerCase();
  if (premise.includes("non-residential")) return "commercial";
  if (premise.includes("residential") && hasUnit) return "apartments";
  if (premise.includes("residential")) return "residential";
  if (hasUnit) return "apartments";
  return "residential";
}

const STATUS_TONE = {
  Active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Inactive: "bg-amber-100 text-amber-700 border-amber-200",
  "De-Energized": "bg-rose-100 text-rose-700 border-rose-200",
};

/**
 * City SEO landing page. Shared component for ES + EN by reading `lang` prop.
 * Routes wired in App.js:
 *   /electricidad-:slug   (lang=es)
 *   /electricity-:slug    (lang=en)
 */
export default function CityPage({ lang = "es", slug: slugProp = null, noLangRedirect = false }) {
  const params = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const slug = slugProp || params.slug;
  const city = CITIES[slug];

  // Hooks MUST be called in the same order on every render, so the
  // useEffect for analytics goes BEFORE the conditional Navigate return.
  // The effect is no-op when the city is unknown (guarded inside).
  useEffect(() => {
    if (!city) return;
    try { trackCityLandingView({ city: city.name, slug: city.slug }); } catch { /* noop */ }
  }, [city]);

  // Language-preference auto-redirect (Feb 2026).
  //
  // When a visitor lands on a city landing whose route language differs from
  // their previously chosen preference, we silently navigate them to the
  // SAME city landing in their language. Why this approach:
  //
  //   • Respects user choice: someone who toggled to ES once stays in ES
  //     even if they click an /electricity-* link from Google Ads.
  //   • Preserves SEO: both /electricity-{slug} (en) and /electricidad-{slug}
  //     (es) URLs still exist and rank independently; we only redirect for
  //     LANGUAGE alignment, not to merge the two.
  //   • No reload: `navigate(..., {replace:true})` is in-app, instant, no flash.
  //   • Idempotent: after redirect, the new route's `lang` prop matches the
  //     i18n.language, so the effect short-circuits via the `current ===
  //     target` guard.
  //
  // First-time visitors (no localStorage) get whatever language i18n detects
  // from their browser; if that matches the route, no redirect happens.
  useEffect(() => {
    if (!city) return;
    if (noLangRedirect) return; // short alias routes (e.g. /houston) stay clean
    const target = lang === "en" ? "en" : "es";
    const current = i18n.language?.startsWith("es") ? "es" : "en";
    if (current === target) return;
    const otherPath = current === "es"
      ? `/electricidad-${city.slug}`
      : `/electricity-${city.slug}`;
    navigate(otherPath, { replace: true });
    // Depend on i18n.language (string) — NOT the i18n object — so this
    // effect re-runs when the user clicks the language toggle on a city
    // page, redirecting them to the matching-language URL automatically.
  }, [city, lang, i18n.language, navigate, noLangRedirect]);

  if (!city) return <Navigate to="/" replace />;

  const L = city[lang] || city.es;
  const isEs = lang === "es";
  const altLangSlug = isEs ? `/electricity-${city.slug}` : `/electricidad-${city.slug}`;
  const myPath = isEs ? `/electricidad-${city.slug}` : `/electricity-${city.slug}`;

  // Build a pre-filled /address URL so users coming from Google Ads land
  // RIGHT on the address-capture screen with their city + ZIP + service
  // type already populated. Previously these CTAs pointed at /enroll,
  // which the address-first guard immediately bounced to /address — the
  // resulting flicker (form briefly visible → redirect) made visitors
  // think the site was broken and explained the high bounce rate on
  // these landings.
  const defaultZip = (city.zips && city.zips[0]) || "";
  const enrollHref = (() => {
    const sp = new URLSearchParams();
    sp.set("city", city.name);
    if (defaultZip) sp.set("zip", defaultZip);
    sp.set("type", "residential");
    sp.set("utm_source", "city_landing");
    sp.set("utm_medium", "cta");
    sp.set("utm_campaign", city.slug);
    return `/address?${sp.toString()}`;
  })();

  const onCtaClick = (position) => () => {
    try { trackCityLandingCtaClick({ city: city.name, slug: city.slug, ctaPosition: position }); } catch { /* noop */ }
  };

  const metaTitle = isEs
    ? `Electricidad ${city.name}, TX · Tarifa fija sin contrato`
    : `${city.name}, TX Electricity · Fixed-rate, no contract`;
  const metaDesc = isEs
    ? `Plan de luz con tarifa fija en ${city.name}, Texas. Distribuidora ${city.tdu.name}. Activación gratis, atención bilingüe, sin fee de cancelación.`
    : `Fixed-rate electricity plan in ${city.name}, Texas. Utility ${city.tdu.name}. Free activation, bilingual support, no cancellation fee.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://velcorenergy.com${myPath}`,
    name: `Velcor Energy — ${city.name}`,
    description: metaDesc,
    url: typeof window !== "undefined" ? window.location.href : `https://velcorenergy.com${myPath}`,
    telephone: "+1-800-715-6516",
    priceRange: `${city.avgRate}¢/kWh`,
    areaServed: { "@type": "City", name: city.name, address: { "@type": "PostalAddress", addressRegion: "TX", addressCountry: "US" } },
    address: { "@type": "PostalAddress", addressLocality: "Houston", addressRegion: "TX", postalCode: "77002", streetAddress: "440 Louisiana St, Ste 1200" },
    sameAs: ["https://www.puc.texas.gov"],
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: L.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <Layout>
      <SEOHead
        title={metaTitle}
        description={metaDesc}
        canonical={myPath}
        ogImage="/hero-day.jpg"
        alternates={[
          { hreflang: isEs ? "en-us" : "es-us", href: altLangSlug },
          { hreflang: "x-default", href: `/electricity-${city.slug}` },
        ]}
        jsonLd={[jsonLd, faqJsonLd]}
      />

      {/* ─── Cinematic Hero with EMBEDDED address bar ─────────────────────
          A visitor coming from Google Ads for a specific city sees their
          city name in giant serif on a premium photo background AND types
          their address right there — zero extra clicks. The market data
          card (Velcor rate / TDU / population / avg bill) moves into a
          compact strip BELOW the hero to keep the eye focused on conversion. */}
      <CityHero
        city={city}
        isEs={isEs}
        enrollHref={enrollHref}
        onCtaClick={onCtaClick("hero_search")}
      />

      {/* ─── Social proof bar — same wording for every city (verifiable claims) */}
      <section
        className="border-y border-slate-200 bg-white"
        data-testid={`city-${city.slug}-social-proof`}
      >
        <div className="max-w-[1180px] mx-auto px-5 py-5 md:py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 text-center">
            {[
              { label: isEs ? "Activación gratis" : "Free activation", testid: "sp-free-act" },
              { label: isEs ? "Sin contratos forzosos" : "No forced contracts", testid: "sp-no-contract" },
              { label: isEs ? "Soporte 100% bilingüe" : "100% bilingual support", testid: "sp-bilingual" },
              { label: isEs ? "Tarifa fija garantizada" : "Fixed-rate guaranteed", testid: "sp-fixed-rate" },
            ].map((it) => (
              <div key={it.testid} className="inline-flex items-center justify-center gap-2" data-testid={it.testid}>
                <CheckCircle2 className="w-4 h-4 text-velcor-green shrink-0" strokeWidth={2.4} />
                <span className="text-velcor-ink text-[12.5px] md:text-[13.5px] font-extrabold tracking-tight">{it.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 3 city-specific USP cards ────────────────────────────────────── */}
      <section className="max-w-[1180px] mx-auto px-5 py-14 md:py-20" data-testid={`city-${city.slug}-usps`}>
        <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-3 text-center">
          {isEs ? `Por qué Velcor en ${city.name}` : `Why Velcor in ${city.name}`}
        </div>
        <h2
          className="display text-velcor-ink text-center max-w-[820px] mx-auto leading-tight mb-12"
          style={{ fontSize: "clamp(26px, 3.6vw, 42px)", fontWeight: 600, letterSpacing: "-0.022em" }}
        >
          {isEs ? "Hecho para tu ciudad. Sin sorpresas." : "Built for your city. No surprises."}
        </h2>
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          <USPCard
            icon={Building2}
            kicker={isEs ? "Tu utility (TDU)" : "Your utility (TDU)"}
            title={city.tdu.name}
            body={
              isEs
                ? `Conectados directamente con ${city.tdu.name} — los mismos cables, mejor servicio. Tu medidor sigue siendo tu medidor; solo cambia quién te factura.`
                : `Connected directly to ${city.tdu.name} — same wires, better service. Your meter stays your meter; only who bills you changes.`
            }
          />
          <USPCard
            icon={Zap}
            kicker={isEs ? "Activación rápida" : "Fast activation"}
            title={isEs ? "24 horas hábiles" : "24 business hours"}
            body={
              isEs
                ? `Cambiar de proveedor en ${city.name} no debe ser una odisea. Verificamos tu medidor (ESIID) al instante y tu nueva tarifa empieza dentro de 24 horas hábiles.`
                : `Switching providers in ${city.name} shouldn't be an odyssey. We verify your meter (ESIID) instantly and your new rate kicks in within 24 business hours.`
            }
          />
          <USPCard
            icon={Phone}
            kicker={isEs ? "Atención bilingüe" : "Bilingual support"}
            title={isEs ? "Hablamos tu idioma" : "We speak your language"}
            body={
              isEs
                ? "Nuestro equipo en Houston te atiende 100% en español o inglés. Sin call-centers offshore, sin esperas eternas. (800) 715-6516."
                : "Our Houston-based team supports you 100% in English or Spanish. No offshore call centers, no eternal waits. (800) 715-6516."
            }
          />
        </div>
      </section>

      {/* ─── Compact market data strip (moved from hero card) ─────────────── */}
      <section className="bg-white border-y border-slate-200" data-testid={`city-${city.slug}-market`}>
        <div className="max-w-[1180px] mx-auto px-5 py-6 md:py-7">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8 text-center md:text-left">
            <Stat icon={Zap} label={isEs ? "Tarifa típica Velcor" : "Typical Velcor rate"} value={`${city.avgRate.toFixed(1)}¢ / kWh`} accent compact />
            <Stat icon={Building2} label={isEs ? "Tu distribuidora" : "Your utility"} value={city.tdu.name} compact />
            <Stat icon={MapPin} label={isEs ? "Población servida" : "Population served"} value={city.population} compact />
            <Stat icon={CheckCircle2} label={isEs ? "Factura promedio" : "Average bill"} value={`$${city.avgBill}`} compact />
          </div>
        </div>
      </section>

      {/* Why-this-city */}
      <section className="bg-[#f6f9f5] py-14 md:py-20">
        <div className="max-w-[1180px] mx-auto px-5">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-3 text-center">
            {isEs ? "Por qué Velcor" : "Why Velcor"}
          </div>
          <h2
            className="display text-velcor-ink text-center max-w-[820px] mx-auto leading-tight"
            style={{ fontSize: "clamp(26px, 3.6vw, 42px)", fontWeight: 600, letterSpacing: "-0.022em" }}
          >
            {L.whyTitle}
          </h2>
          <p className="mt-5 text-slate-500 text-[15px] md:text-[16px] leading-relaxed max-w-[720px] mx-auto text-center">
            {L.whyBody}
          </p>
        </div>
      </section>

      {/* Reusable billing transparency block */}
      <BillingTransparency />

      {/* Neighborhoods + ZIP grid (SEO-rich) */}
      <section className="max-w-[1180px] mx-auto px-5 py-14 md:py-20" data-testid={`city-zips-${city.slug}`}>
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-3">
              {isEs ? "Vecindarios" : "Neighborhoods"}
            </div>
            <h3
              className="display text-velcor-ink leading-tight mb-5"
              style={{ fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 600 }}
            >
              {isEs ? `Damos servicio en todo ${city.name}` : `We serve all of ${city.name}`}
            </h3>
            <div className="flex flex-wrap gap-2">
              {city.neighborhoods.map((n) => (
                <span
                  key={n}
                  className="inline-flex px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-[12.5px] font-medium"
                >
                  {n}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-3">
              {isEs ? "Códigos postales servidos" : "ZIP codes served"}
            </div>
            <h3
              className="display text-velcor-ink leading-tight mb-5"
              style={{ fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 600 }}
            >
              {city.zips.length}+ ZIPs {isEs ? "en" : "in"} {city.name}
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1.5 max-h-[260px] overflow-y-auto pr-2">
              {city.zips.map((z) => (
                <span
                  key={z}
                  className="text-[11.5px] tabular-nums font-mono text-slate-500 px-2 py-1 rounded border border-slate-100 bg-white text-center"
                >
                  {z}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — redesigned with numbered items, hover lift, open-state accent
          rail, and a closing CTA card. The structured-data JSON-LD above is
          unchanged so SEO ranking stays intact. */}
      <section className="bg-[#f6f9f5] py-14 md:py-20" data-testid={`city-faq-section-${city.slug}`}>
        <div className="max-w-[860px] mx-auto px-5">
          {/* Section header */}
          <div className="text-center mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-3">
              <span className="w-6 h-px bg-velcor-green/40" />
              FAQ
              <span className="w-6 h-px bg-velcor-green/40" />
            </div>
            <h2
              className="display text-velcor-ink leading-tight"
              style={{ fontSize: "clamp(26px, 3.6vw, 40px)", fontWeight: 600, letterSpacing: "-0.022em" }}
            >
              {isEs ? `Preguntas frecuentes — ${city.name}` : `Frequently asked — ${city.name}`}
            </h2>
            <p className="mt-4 text-slate-500 text-[14px] md:text-[15px] max-w-[560px] mx-auto leading-relaxed">
              {isEs
                ? `Todo lo que un vecino de ${city.name} pregunta antes de cambiarse — respondido en español claro.`
                : `Everything ${city.name} neighbors ask before switching — answered in plain English.`}
            </p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-[11.5px] font-bold text-velcor-green bg-velcor-green/10 px-3 py-1 rounded-full">
              <span>{L.faqs.length}</span>
              <span className="opacity-80">{isEs ? "preguntas resueltas" : "questions answered"}</span>
            </div>
          </div>

          {/* FAQ items */}
          <dl className="space-y-3">
            {L.faqs.map((f, i) => (
              <details
                key={i}
                className="group bg-white border border-slate-200/80 rounded-2xl overflow-hidden transition-all duration-200 hover:border-velcor-green/40 hover:shadow-[0_8px_24px_-12px_rgba(15,81,50,0.18)] hover:-translate-y-px open:border-velcor-green/60 open:shadow-[0_8px_24px_-12px_rgba(15,81,50,0.22)] open:bg-gradient-to-br open:from-white open:to-[#f1f7ed]"
                data-testid={`city-faq-${i}`}
              >
                <summary className="flex items-start gap-4 md:gap-5 px-5 md:px-6 py-5 md:py-6 cursor-pointer list-none relative">
                  {/* Open-state lime accent rail */}
                  <span
                    aria-hidden
                    className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#d8e465] to-velcor-green opacity-0 group-open:opacity-100 transition-opacity"
                  />
                  {/* Number indicator — larger, two-color (lime + green) for
                      maximum visibility against the white card. */}
                  <span
                    className="flex-shrink-0 w-10 md:w-12 font-mono text-[15px] md:text-[17px] font-extrabold tracking-[0.04em] pt-[2px]"
                    style={{
                      color: "#2f7a3a",
                      textShadow: "0 1px 0 rgba(216,228,101,0.45)",
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {/* Question */}
                  <span className="flex-1 text-velcor-ink font-bold text-[15px] md:text-[16.5px] leading-snug">
                    {f.q}
                  </span>
                  {/* Chevron */}
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 group-hover:bg-velcor-green/15 group-open:bg-velcor-green/20 flex items-center justify-center transition-colors">
                    <ChevronRight className="w-3.5 h-3.5 text-velcor-green transition-transform group-open:rotate-90" />
                  </span>
                </summary>
                <div className="px-5 md:px-6 pb-6 md:pb-7 pt-0 text-slate-600 text-[14px] md:text-[14.5px] leading-relaxed md:pl-[72px]">
                  {f.a}
                </div>
              </details>
            ))}
          </dl>

          {/* Closing CTA — "still have questions?" with phone + WhatsApp pills */}
          <div
            className="mt-8 md:mt-10 bg-gradient-to-br from-[#2f7a3a]/10 to-[#d8e465]/20 border border-velcor-green/25 rounded-2xl px-6 py-5 md:px-8 md:py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
            data-testid={`city-faq-cta-${city.slug}`}
          >
            <div className="flex-1">
              <div className="font-bold text-velcor-ink text-[15px] md:text-[16.5px]">
                {isEs ? "¿No encuentras lo que buscas?" : "Didn't find what you're looking for?"}
              </div>
              <div className="text-slate-600 text-[13px] md:text-[13.5px] mt-0.5">
                {isEs
                  ? "Habla con un asesor humano — sin bots, sin esperas largas."
                  : "Talk to a human advisor — no bots, no long waits."}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <a
                href="tel:+18007156516"
                className="inline-flex items-center gap-2 bg-velcor-ink hover:bg-velcor-green text-white font-bold text-[13px] md:text-[13.5px] px-5 md:px-6 py-2.5 rounded-full transition-colors shadow-sm"
                data-testid={`city-faq-cta-call-${city.slug}`}
              >
                <Phone className="w-3.5 h-3.5" />
                <span>{isEs ? "Llamar" : "Call"}</span>
              </a>
              <a
                href={whatsappLink(isEs ? "Hola, tengo una duda sobre Velcor en " + city.name : "Hi, I have a question about Velcor in " + city.name)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-[13px] md:text-[13.5px] px-4 md:px-5 py-2.5 rounded-full transition-colors shadow-sm"
                data-testid={`city-faq-cta-whatsapp-${city.slug}`}
              >
                <MessageCircle className="w-3.5 h-3.5 fill-white" />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Bottom CTA with EMBEDDED address bar (mirrors hero) ─────────── */}
      <section
        className="relative bg-velcor-cream"
        data-testid={`city-${city.slug}-cta-bottom-section`}
      >
        {/* Soft mint radial glow on the cream background — keeps the section
            airy and identifies the brand without competing with the CTA. */}
        <div
          aria-hidden
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(900px 420px at 50% 35%, rgba(47,122,58,0.10) 0%, rgba(248,252,249,0) 70%)",
          }}
        />
        <div className="relative z-10 max-w-[820px] mx-auto px-5 py-16 md:py-24 pb-24 md:pb-36 text-center text-slate-900">
          <h2
            className="font-black leading-tight"
            style={{
              fontFamily: '"Fraunces", "Source Serif Pro", Georgia, serif',
              fontSize: "clamp(28px, 4vw, 44px)",
              letterSpacing: "-0.022em",
              color: "#0a1628",
            }}
          >
            {isEs ? `¿Listo, ${city.name}?` : `Ready, ${city.name}?`}
          </h2>
          <div className="mt-10">
            <CityAddressBar
              city={city}
              isEs={isEs}
              enrollHref={enrollHref}
              onSubmit={onCtaClick("cta_bottom_search")}
              variant="light"
              testid={`city-${city.slug}-bottom-bar`}
            />
            <div className="mt-5 text-slate-500 text-[12.5px] flex items-center justify-center gap-2">
              <Phone className="w-3.5 h-3.5" />
              <span>{isEs ? "¿Prefieres hablar?" : "Prefer to talk?"}</span>
              <a href="tel:+18007156516" className="font-bold text-velcor-forest hover:text-velcor-emerald transition-colors">
                (800) 715-6516
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function Stat({ icon: Icon, label, value, accent = false, compact = false }) {
  // `compact` shrinks the icon + tightens spacing for the inline market
  // strip; the original block layout is used in the hero card.
  return (
    <div className={`flex items-start gap-${compact ? "2.5" : "3.5"}`}>
      <span
        className={`grid place-items-center ${compact ? "w-8 h-8 rounded-lg" : "w-10 h-10 rounded-xl"} shrink-0 ${accent ? "bg-velcor-green text-white" : "bg-slate-100 text-slate-500"}`}
      >
        <Icon className={compact ? "w-4 h-4" : "w-4.5 h-4.5"} strokeWidth={1.7} />
      </span>
      <div className="min-w-0">
        <div className={`${compact ? "text-[10px]" : "text-[11px]"} font-extrabold uppercase tracking-[0.16em] text-slate-400`}>{label}</div>
        <div className={`${compact ? "text-[14px]" : "text-[16px]"} font-extrabold mt-0.5 ${accent ? "text-velcor-green" : "text-velcor-ink"} truncate`}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// CityHero — cinematic full-bleed top section with the city name in giant
// serif and an embedded address search bar. Mirrors the homepage hero
// pattern so Google-Ads traffic recognizes the conversion mechanic
// instantly. Background image falls back to `/hero-homes.jpg` if the
// city doesn't define its own.
// ──────────────────────────────────────────────────────────────────────
function CityHero({ city, isEs, enrollHref, onCtaClick }) {
  const bgUrl = city.heroImage || "/hero-homes.jpg";
  const kicker = isEs ? `Electricidad en ${city.name}` : `${city.name} electricity`;
  // Headline + subhead are intentionally inline (no i18n key lookup) so
  // we can interpolate the city name freely and ship copy variants per
  // service-mix without touching the locale JSON.
  const headline = isEs ? `${city.name}, tu luz a precio fijo.` : `${city.name}, fixed-price power.`;
  const subhead = isEs
    ? `Plan único con ${city.tdu.name}. Activación gratis, sin contratos forzosos, atención 100% bilingüe.`
    : `One plan with ${city.tdu.name}. Free activation, no forced contracts, 100% bilingual support.`;
  return (
    <section className="relative" style={{ background: "#0a1229" }} data-testid={`city-hero-${city.slug}`}>
      {/* Background layers live in their own clipped sub-div so the kenburns
          scale animation doesn't overflow, while the foreground content
          (including the autocomplete dropdown) is NOT clipped — the dropdown
          can render below the hero's bottom edge without being cut off. */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 hero-kenburns"
          style={{ backgroundImage: `url(${bgUrl})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,18,41,0.62) 0%, rgba(10,18,41,0.42) 45%, rgba(10,18,41,0.22) 75%, rgba(10,18,41,0.08) 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none mix-blend-overlay"
          style={{
            opacity: 0.05,
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
          }}
        />
      </div>
      <div className="relative z-10 max-w-[980px] mx-auto px-5 pt-16 md:pt-24 pb-12 md:pb-16 text-center text-white">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-[10.5px] font-extrabold uppercase tracking-[0.22em] mb-5"
          style={{ color: "#bce5c6" }}
          data-testid={`city-hero-${city.slug}-kicker`}
        >
          <MapPin className="w-3.5 h-3.5" /> {kicker}
        </div>
        <h1
          className="font-black leading-[1.05] max-w-[820px] mx-auto"
          style={{
            fontFamily: '"Fraunces", "Source Serif Pro", Georgia, serif',
            fontSize: "clamp(34px, 5.4vw, 60px)",
            letterSpacing: "-0.022em",
            textShadow: "0 2px 18px rgba(0,0,0,0.4)",
          }}
          data-testid={`city-hero-${city.slug}-title`}
        >
          {headline}
        </h1>
        <p
          className="mt-5 text-white/90 text-[14px] md:text-[16px] max-w-[620px] mx-auto leading-relaxed"
          style={{ textShadow: "0 1px 10px rgba(0,0,0,0.35)" }}
          data-testid={`city-hero-${city.slug}-sub`}
        >
          {subhead}
        </p>
        <div className="mt-9 max-w-[760px] mx-auto">
          <CityAddressBar
            city={city}
            isEs={isEs}
            enrollHref={enrollHref}
            onSubmit={onCtaClick}
            variant="light"
            testid={`city-hero-${city.slug}-bar`}
          />
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-white/80 text-[12px]">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-velcor-mint" /> {isEs ? "Sin tarjeta para ver tu precio" : "No card to see your price"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-velcor-mint" /> {isEs ? "Menos de 30 segundos" : "Under 30 seconds"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-velcor-mint" /> {isEs ? "Cambio gratis" : "Free switch"}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// CityAddressBar — pill input with INLINE autocomplete (Feb 2026).
//
// Hits the SAME ComparePower ESIID endpoint as the homepage hero
// (`POST /api/comparepower/esiid_lookup`) with a 220ms debounce, then
// shows a dropdown of candidates directly under the input — exactly
// the same UX visitors see on velcorenergy.com home. Picking a candidate
// auto-detects service type (residential/apartments/commercial via
// premise_type heuristic) and navigates to the matching plan page with
// canonical zip/esiid/utility persisted to localStorage + URL params.
//
// On the lit/dark visual variants: `light` is the white pill used in the
// hero (inside dark photo background); `dark` is glass-morphic for the
// bottom CTA (sitting on top of an already-dark navy section).
// ──────────────────────────────────────────────────────────────────────
function CityAddressBar({ city, isEs, enrollHref, onSubmit, variant = "light", testid }) {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const debounceRef = useRef();
  const defaultZip = (city.zips && city.zips[0]) || "";
  // Same placeholder copy used across the entire site (home hero + /address)
  // so the funnel feels coherent — no scary, city-specific examples that
  // confuse visitors typing an address from a different metro.
  const placeholder = isEs ? "Ingresa la dirección de tu servicio" : "Enter your service address";
  // Distinguish funnel source: hero bar vs bottom CTA bar.
  const gaSource = (testid || "").includes("bottom") ? "city_bottom" : "city_hero";

  const triggerLookup = async (val) => {
    const v = (val ?? value).trim();
    if (v.length < 3) return;
    setSearching(true);
    // Funnel step 1/5 — fire once per intent (when API call starts).
    trackAddressSearch({ source: gaSource, hasZip: !!defaultZip, city: city.slug });
    try {
      // City + ZIP are intentionally BLANK so the user can search any TX
      // address (not just within the landing city). This mirrors the
      // homepage hero behaviour — if we pass the city, ComparePower hard-
      // filters and returns 0 candidates whenever the typed address sits
      // in a different metro (e.g. user landed on /electricidad-san-antonio
      // but typed a Houston street). The picked candidate carries its own
      // canonical city/zip so downstream attribution is still accurate.
      const { data } = await api.post("/comparepower/esiid_lookup", {
        address: v,
        city: "",
        state: "TX",
        zip: "",
      });
      setCandidates(data?.candidates || []);
    } catch {
      setCandidates([]);
    } finally {
      setSearching(false);
    }
  };

  const onChange = (val) => {
    setValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length >= 3) {
      debounceRef.current = setTimeout(() => triggerLookup(val), 220);
    } else {
      setCandidates([]);
    }
  };

  const handlePick = async (c) => {
    try { onSubmit && onSubmit({ preventDefault: () => {} }); } catch { /* noop */ }
    const detectedType = detectServiceType(c.address, c.premise_type);
    const zip = c.zip || defaultZip || "";

    // RentCast enrichment — silent, optional. Shared with Home hero +
    // /address page so visitors landing on /electricidad-houston get the
    // same plan-tier auto-detection as those starting from the homepage.
    const { routedType, profileId } = await enrichWithRentCast({
      address: c.address,
      city: c.city || "",
      zip,
      type: detectedType,
    });

    // Persist so the rest of the funnel (address page, plans, enroll)
    // picks up the same context the user just selected.
    try {
      localStorage.setItem("velcor_zip", zip);
      localStorage.setItem("velcor_service_type", routedType);
      localStorage.setItem("velcor_service_address", c.address);
      localStorage.setItem("velcor_esiid", c.esiid || "");
      localStorage.setItem("velcor_utility", c.utility_name || "");
      if (c.city) localStorage.setItem("velcor_city", c.city);
    } catch { /* noop */ }

    // Forward UTM params from the original enrollHref so attribution
    // survives the candidate pick.
    const q = new URLSearchParams();
    try {
      const orig = new URL(enrollHref, window.location.origin);
      orig.searchParams.forEach((v, k) => {
        if (k.startsWith("utm_")) q.set(k, v);
      });
    } catch { /* noop */ }
    q.set("zip", zip);
    q.set("type", routedType);
    q.set("address", c.address);
    if (c.city) q.set("city", c.city);
    if (c.esiid) q.set("esiid", c.esiid);
    if (c.utility_name) q.set("utility", c.utility_name);
    if (profileId) q.set("profile", profileId);

    // Land on the matching plan page — same destination as the homepage
    // hero — so the conversion mechanic from city landings matches what
    // tested users already expect.
    navigate(`/${routedType}?${q.toString()}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // If we already have candidates, picking the first one is the
    // "Enter" affordance. Otherwise force a fresh lookup.
    if (candidates.length > 0) {
      handlePick(candidates[0]);
    } else if (value.trim().length >= 3) {
      triggerLookup(value);
    }
  };

  const isLight = variant === "light";

  return (
    <div className="relative w-full" data-testid={`${testid}-wrapper`}>
      <form
        onSubmit={handleSubmit}
        className={`rounded-[22px] md:rounded-full grid md:flex md:items-center gap-3 p-2.5 md:p-2 md:pl-5 ${
          isLight
            ? "bg-white border border-white/40"
            : "bg-white/10 backdrop-blur-md border border-white/30"
        }`}
        style={{ boxShadow: isLight ? "0 16px 40px rgba(0,0,0,0.25)" : "0 12px 32px rgba(0,0,0,0.35)" }}
        data-testid={testid}
      >
        <span
          className={`hidden md:grid place-items-center rounded-full shrink-0 ${
            isLight ? "bg-velcor-green/15 text-velcor-green" : "bg-white/15 text-white"
          }`}
          style={{ width: 44, height: 44 }}
        >
          <MapPin className="w-5 h-5" />
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`flex-1 min-w-0 bg-transparent outline-none px-2 py-2.5 md:py-0 text-[15px] font-bold min-h-[48px] ${
            isLight ? "text-velcor-ink placeholder:text-slate-400" : "text-white placeholder:text-white/60"
          }`}
          autoComplete="street-address"
          data-testid={`${testid}-input`}
        />
        {searching && (
          <span className={`hidden md:flex items-center pr-2 ${isLight ? "text-velcor-green" : "text-white"}`}>
            <Loader2 className="w-4 h-4 animate-spin" />
          </span>
        )}
        <button
          type="submit"
          className="btn-green min-h-[46px] px-5 text-[14px]"
          data-testid={`${testid}-submit`}
        >
          {isEs ? "Ver mi precio" : "See my price"}
          <ChevronRight className="w-4 h-4" />
        </button>
      </form>

      {/* Live candidates dropdown — same shape as homepage hero, anchored
          below the pill. Z-50 so it floats over downstream sections. */}
      <AnimatePresence>
        {candidates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.25 }}
            className="absolute left-0 right-0 top-full mt-3 z-50 bg-white rounded-2xl border border-slate-200 p-3 md:p-4 shadow-[0_24px_60px_rgba(15,23,42,0.22)]"
            data-testid={`${testid}-results`}
          >
            <div className="flex items-center justify-between mb-2.5 px-1">
              <span className="text-velcor-ink text-[12.5px] font-extrabold">
                {isEs ? "Direcciones encontradas" : "Addresses found"}
                <span className="text-slate-400 ml-1.5">({candidates.length})</span>
              </span>
              <span className="text-slate-400 text-[10.5px] font-bold uppercase tracking-wider">
                {isEs ? "Detección automática" : "Auto-detect"}
              </span>
            </div>
            <div className="grid gap-1.5 max-h-[360px] overflow-y-auto no-scrollbar">
              {candidates.map((c) => {
                const dtype = detectServiceType(c.address, c.premise_type);
                const tone = STATUS_TONE[c.status] || STATUS_TONE.Active;
                const ServiceIcon = dtype === "apartments" ? Building2 : dtype === "commercial" ? Briefcase : HomeIcon;
                return (
                  <button
                    key={c.esiid}
                    type="button"
                    onClick={() => handlePick(c)}
                    data-testid={`${testid}-candidate-${c.esiid}`}
                    className="text-left rounded-xl px-3 py-2.5 border-2 border-slate-200 bg-white hover:border-velcor-green hover:shadow-[0_10px_24px_rgba(47,122,58,0.12)] hover:-translate-y-0.5 transition-all group"
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        className="grid place-items-center rounded-lg shrink-0 bg-velcor-green/12 text-velcor-green"
                        style={{ width: 32, height: 32 }}
                      >
                        <ServiceIcon className="w-3.5 h-3.5" strokeWidth={2} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-extrabold text-velcor-ink text-[13.5px] truncate">{c.address}</div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-velcor-green/10 text-velcor-green border border-velcor-green/20">
                            {dtype === "apartments"
                              ? (isEs ? "Apto" : "Apt")
                              : dtype === "commercial"
                              ? (isEs ? "Comercial" : "Commercial")
                              : (isEs ? "Casa" : "Home")}
                          </span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-slate-100 text-slate-700 border border-slate-200 truncate max-w-[160px]">
                            {c.utility_name}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-extrabold border ${tone}`}>
                            {c.status === "Active" ? (isEs ? "Activo" : "Active") :
                             c.status === "Inactive" ? (isEs ? "Inactivo" : "Inactive") :
                             c.status === "De-Energized" ? (isEs ? "Desconectado" : "De-energized") :
                             c.status}
                          </span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-velcor-green transition-colors shrink-0" />
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// USPCard — uniform 3-up card used in the "Why Velcor in {city}" band.
// ──────────────────────────────────────────────────────────────────────
function USPCard({ icon: Icon, kicker, title, body }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7 hover:border-velcor-green/40 hover:shadow-[0_8px_24px_rgba(47,122,58,0.08)] transition-all">
      <span className="grid place-items-center w-11 h-11 rounded-xl bg-velcor-green/10 text-velcor-green mb-4">
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </span>
      <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-slate-400 mb-1.5">{kicker}</div>
      <div className="text-velcor-ink font-extrabold text-[18px] md:text-[19px] tracking-tight mb-2.5">{title}</div>
      <p className="text-slate-500 text-[13.5px] leading-relaxed">{body}</p>
    </div>
  );
}
